import { Avatar, Divider } from "@mui/material";
import { useState, useEffect } from "react";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useOnline } from '../../context/OnlineContext';
import { isSignInWithEmailLink } from "firebase/auth";
import { auth } from "../../firebase";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useDispatch, useSelector } from 'react-redux';
import { loadInitialSettings } from '../chartSettings/ChartSettingsSlice';
import { loadRWGPSAuth, loginWithGoogle, logoutUser, sendEmailLoginLink, completeEmailSignIn } from './AuthSlice';
import { useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';

function Login() {
    const user = useSelector(state => state.auth.user);
    const { connections } = useSelector(state => state.auth);
    const [anchorElUser, setAnchorElUser] = useState(null);
    const { isOnline } = useOnline();
    const [openEmailDialog, setOpenEmailDialog] = useState(false);
    const [email, setEmail] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const dispatch = useDispatch();
    const { settingsLoaded } = useSelector(state => state.chartSettings);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        // Check if this is an email sign-in link
        if (isSignInWithEmailLink(auth, window.location.href)) {
            dispatch(completeEmailSignIn());
        }
        if (user) {
            // Only load settings if they haven't been loaded yet
            if (!settingsLoaded) {
                dispatch(loadInitialSettings());
            }
            if (!connections.rwgps) {
                dispatch(loadRWGPSAuth());
            }
        }
    }, [dispatch, user, settingsLoaded, connections.rwgps]);

    function handleLogin() {
        dispatch(loginWithGoogle());
    }

    function handleLogout() {
        if (!isOnline) {
            return;
        }
        dispatch(logoutUser());
        handleCloseUserMenu();
    }

    const handleOpenUserMenu = (event) => {
        setAnchorElUser(event.currentTarget);
    };

    const handleCloseUserMenu = () => {
        setAnchorElUser(null);
    };

    const handleEmailLogin = async () => {
        const result = await dispatch(sendEmailLoginLink(email));
        if (result.payload?.success) {
            setEmailSent(true);
        } else {
            // Handle error
            console.error(result.payload?.error);
        }
    };


    return (
        <Box sx={{ flexGrow: 0 }}>
            {user ? (
                <Tooltip title={!isOnline ? "App is offline" : "User menu"}>
                    <IconButton
                        onClick={handleOpenUserMenu}
                        sx={{
                            p: 0,
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }
                        }}
                    >
                        <Avatar
                            alt={user.displayName}
                            src={user.photoURL}
                            sx={{
                                width: { xs: 36, sm: 40 },
                                height: { xs: 36, sm: 40 },
                                border: !isOnline ? '2px solid #e67e22' : 'none'
                            }}
                        />
                    </IconButton>
                </Tooltip>
            ) : (
                isMobile ? (
                    <IconButton
                        onClick={handleOpenUserMenu}
                        color="inherit"
                        sx={{
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                            }
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                ) : (
                    <Button
                        onClick={handleOpenUserMenu}
                        color="inherit"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.2)'
                            }
                        }}
                    >
                        Login
                    </Button>
                )
            )}
            <Menu
                sx={{
                    mt: '45px',
                    '& .MuiPaper-root': {
                        minWidth: { xs: 200, sm: 220 },
                        borderRadius: 2,
                        boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                        border: '1px solid #e9ecef'
                    }
                }}
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
            >
                {user ? (
                    // Logged-in user menu
                    <>
                        <MenuItem
                            onClick={() => { handleCloseUserMenu(); navigate('/settings'); }}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <Typography sx={{ textAlign: 'center', fontWeight: 500 }}>App Settings</Typography>
                        </MenuItem>
                        <MenuItem
                            onClick={() => { handleCloseUserMenu(); navigate('/pricing'); }}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <Typography sx={{ textAlign: 'center', fontWeight: 500 }}>Subscription</Typography>
                        </MenuItem>
                        <Divider />
                        <MenuItem
                            onClick={handleLogout}
                            disabled={!isOnline}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <Typography sx={{ textAlign: 'center', fontWeight: 500 }}>Logout</Typography>
                        </MenuItem>
                    </>
                ) : (
                    // Non-logged-in user menu
                    <>
                        <MenuItem
                            onClick={() => { handleCloseUserMenu(); navigate('/'); }}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <Typography sx={{ textAlign: 'center', fontWeight: 500 }}>Home</Typography>
                        </MenuItem>
                        <MenuItem
                            onClick={() => { handleCloseUserMenu(); navigate('/pricing'); }}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <Typography sx={{ textAlign: 'center', fontWeight: 500 }}>Pricing</Typography>
                        </MenuItem>
                        <MenuItem
                            onClick={() => { handleCloseUserMenu(); navigate('/help'); }}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <Typography sx={{ textAlign: 'center', fontWeight: 500 }}>Help</Typography>
                        </MenuItem>
                        <Divider />
                        <MenuItem
                            onClick={() => { handleCloseUserMenu(); handleLogin(); }}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <Typography sx={{ textAlign: 'center', fontWeight: 500 }}>Login with Google</Typography>
                        </MenuItem>
                        <MenuItem
                            onClick={() => { handleCloseUserMenu(); setOpenEmailDialog(true); }}
                            sx={{
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                }
                            }}
                        >
                            <Typography sx={{ textAlign: 'center', fontWeight: 500 }}>Login with Email</Typography>
                        </MenuItem>
                    </>
                )}
            </Menu>

            <Dialog open={openEmailDialog} onClose={() => setOpenEmailDialog(false)}>
                <DialogTitle>
                    {emailSent ? "Check your email" : "Login with Email"}
                </DialogTitle>
                <DialogContent>
                    {emailSent ? (
                        <Typography>
                            We've sent a login link to {email}. Click the link in the email to sign in.
                        </Typography>
                    ) : (
                        <TextField
                            autoFocus
                            margin="dense"
                            id="email"
                            label="Email Address"
                            type="email"
                            fullWidth
                            variant="outlined"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setOpenEmailDialog(false); setEmailSent(false); }}>
                        Close
                    </Button>
                    {!emailSent && (
                        <Button onClick={handleEmailLogin}>
                            Send Login Link
                        </Button>
                    )}
                </DialogActions>
            </Dialog>


        </Box>
    );
}

export default Login;